import { useCallback, useState, useContext } from 'react';
import axios from '@helpers/axios';
import { ClaimedPlayer } from '@features/Home/claimedPlayer';
import { TeamSummary } from '@features/Home/home.d';
import * as Sentry from '@sentry/react';
import { UserContext } from '@services/Session';
import { shd } from '@shd/jslib/models';

import { compressImage } from '@features/Settings/TeamProfile/imgUploadHelper';

const useClaimedPlayer = (claimedPlayerId: string, closeModal?: () => void) => {
  const [claimedPlayer, setClaimedPlayer] = useState<ClaimedPlayer>();
  const [claimedPlayerTeams, setClaimedPlayerTeams] = useState<TeamSummary[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const userStore = useContext(UserContext);

  const updateStore = useCallback(
    (player: ClaimedPlayer) => {
      const p = { ...userStore.selectedPlayer, ...player };
      userStore.setSelectedPlayer(p as shd.ClaimedPlayer);
      const players = userStore.userPlayers.map((storePlayer) => {
        if (storePlayer._id === p._id) {
          return p;
        }
        return storePlayer;
      });
      userStore.setUserPlayers(players as shd.ClaimedPlayer[]);
    },
    [userStore]
  );

  const fetchClaimedPlayer = useCallback(async () => {
    setLoading(true);
    await axios
      .get(`/v2/claimed_players/${claimedPlayerId}`)
      .then((response) => {
        const { player, teams } = response.data;
        setClaimedPlayer(player);
        setClaimedPlayerTeams(teams);
        updateStore(player);
        setError(null);
      })
      .catch((err) => {
        Sentry.captureException(err);
        setError(
          `Failed to fetch claimed players and teams. Please try again. Error: ${err}`
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [claimedPlayerId, updateStore]);

  const updateClaimedPlayer = useCallback(
    async (updatedPlayer: ClaimedPlayer) => {
      setLoading(true);
      axios
        .post(`/v2/claimed_players/${claimedPlayerId}`, { ...updatedPlayer })
        .then((response) => {
          const { player, teams } = response.data;
          setClaimedPlayer(player);
          setClaimedPlayerTeams(teams);
          updateStore(player);
          setError(null);
          closeModal && closeModal();
        })
        .catch((err) => {
          Sentry.captureException(err);
          setError(
            `Failed to update claimed player. Please try again. Error: ${err}`
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [claimedPlayerId, updateStore, closeModal]
  );

  const uploadPlayerImage = useCallback(
    async (image: File) => {
      const compressed = await compressImage(image);
      const formData = new FormData();
      // The third parameter is required for server
      formData.append('image', compressed, (compressed as File).name);
      formData.append('category', '200'); // 200 is the category for player profile images
      const uploaded = await axios
        .post(`/api/player/${claimedPlayerId}/image`, formData, {
          headers: {
            'content-type': 'multipart/form-data',
            responseType: 'arraybuffer',
          },
        })
        .then((response) => {
          return response.data;
        })
        .catch((err) => {
          Sentry.captureException(err);
          setError(`Imaged failed to upload. Please try again. Error: ${err}`);
        });
      return uploaded;
    },
    [claimedPlayerId]
  );

  return {
    updateClaimedPlayer,
    fetchClaimedPlayer,
    uploadPlayerImage,
    claimedPlayer,
    claimedPlayerTeams,
    loading,
    error,
  };
};

export default useClaimedPlayer;
